import { useState, useRef, useEffect } from 'react';
import { useSessionStore }   from '../store/sessionStore.js';
import { createCashMovement } from '../api/pos.api.js';
import { IcoClose, IcoArrowUp, IcoArrowDown } from './common/Icons.jsx';
import styles from './CashMovementModal.module.css';

const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

const CONCEPTS_IN  = ['Fondo de cambio', 'Depósito', 'Apertura adicional', 'Otro'];
const CONCEPTS_OUT = ['Pago a proveedor', 'Retiro parcial', 'Gastos', 'Otro'];

export default function CashMovementModal({ onClose, onSaved }) {
  const session      = useSessionStore((s) => s.session);
  const updateTotals = useSessionStore((s) => s.updateSessionTotals);

  const [type,    setType]    = useState('in');
  const [amount,  setAmount]  = useState('');
  const [concept, setConcept] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState(null);
  const amountRef = useRef(null);

  useEffect(() => { setTimeout(() => amountRef.current?.focus(), 80); }, []);

  const concepts = type === 'in' ? CONCEPTS_IN : CONCEPTS_OUT;
  const amountNum = Math.max(0, Number(amount) || 0);

  const handleSave = async () => {
    if (!amountNum || amountNum <= 0) { setError('Ingresa un monto válido'); return; }
    setError(null);
    setSaving(true);
    try {
      await createCashMovement(session._id, { type, amount: amountNum, concept: concept.trim() });
      // Update expected_cash in sessionStore so the header stats stay accurate
      updateTotals({
        expected_cash: (session.expected_cash ?? 0) + (type === 'in' ? amountNum : -amountNum),
      });
      onSaved?.({ type, amount: amountNum, concept });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error ?? 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2 className={styles.title}>Movimiento de caja</h2>
          <button className={styles.closeBtn} onClick={onClose}><IcoClose size={13} /></button>
        </div>

        {/* Tipo */}
        <div className={styles.typeRow}>
          <button
            className={`${styles.typeBtn} ${type === 'in' ? styles.typeBtnIn : ''}`}
            onClick={() => { setType('in'); setConcept(''); }}
          >
            <span className={styles.typeBtnIcon}><IcoArrowUp size={20} /></span>
            Entrada
          </button>
          <button
            className={`${styles.typeBtn} ${type === 'out' ? styles.typeBtnOut : ''}`}
            onClick={() => { setType('out'); setConcept(''); }}
          >
            <span className={styles.typeBtnIcon}><IcoArrowDown size={20} /></span>
            Salida
          </button>
        </div>

        {/* Monto */}
        <div className={styles.field}>
          <label className={styles.label}>Monto</label>
          <div className={styles.amountRow}>
            <span className={styles.currencySymbol}>$</span>
            <input
              ref={amountRef}
              className={`input ${styles.amountInput}`}
              type="number"
              min="0.01"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              style={{ paddingLeft: 32, fontSize: 24, fontFamily: 'var(--f-mono)' }}
            />
          </div>
          {/* Denominaciones rápidas */}
          <div className={styles.quickAmounts}>
            {[50, 100, 200, 500].map((v) => (
              <button key={v} className={styles.quickBtn} onClick={() => setAmount(String(v))}>
                ${v}
              </button>
            ))}
          </div>
        </div>

        {/* Concepto */}
        <div className={styles.field}>
          <label className={styles.label}>Concepto</label>
          <div className={styles.conceptChips}>
            {concepts.map((c) => (
              <button
                key={c}
                className={`${styles.chip} ${concept === c ? styles.chipActive : ''}`}
                onClick={() => setConcept((p) => (p === c ? '' : c))}
              >
                {c}
              </button>
            ))}
          </div>
          <input
            className={`input ${styles.conceptInput}`}
            type="text"
            placeholder="O escribe un concepto…"
            value={concept}
            maxLength={80}
            onChange={(e) => setConcept(e.target.value)}
          />
        </div>

        {error && <p className={styles.error}>{error}</p>}

        {/* Resumen */}
        {amountNum > 0 && (
          <div className={`${styles.summary} ${type === 'in' ? styles.summaryIn : styles.summaryOut}`}>
            <span>{type === 'in' ? <><IcoArrowUp size={13} /> Entrada</> : <><IcoArrowDown size={13} /> Salida</>}</span>
            <strong>{fmt(amountNum)}</strong>
          </div>
        )}

        <button
          className={`btn btn--primary btn--full ${styles.saveBtn}`}
          onClick={handleSave}
          disabled={saving || !amountNum}
        >
          {saving ? <span className="spinner" /> : 'Registrar movimiento'}
        </button>
      </div>
    </div>
  );
}
